/**
 * Formulare mit Ausgangsstand (H1.3b, E-039).
 *
 * Ein Formular, das einen gespeicherten Datensatz bearbeitet, merkt sich die
 * Werte beim Öffnen — den Ausgangsstand. Daraus folgen drei Regeln:
 *
 * 1. Gespeichert wird nur, was der Nutzer gegenüber dem Ausgangsstand
 *    geändert hat (`eigeneAenderungen`). Ein Feld, das er nicht angefasst hat,
 *    schreibt er auch nicht — und kann so keine fremde Änderung überschreiben.
 *
 * 2. Kommt ein frischer Stand an (nach dem Speichern einer Notiz, eines
 *    Belegs, nach einem Konflikt), folgen die nicht angefassten Felder ihm
 *    still. Die eigenen Eingaben bleiben stehen (`abgleichen`).
 *
 * 3. Hat jemand anderes ein Feld geändert, das man selbst geändert hat, wird
 *    nicht geraten: Das Feld kommt als Konflikt zurück, und der Nutzer
 *    entscheidet („Übernehmen" / „Meins behalten").
 *
 * ═══ WARUM WEDER `key = geaendert_am` NOCH BLOSS `key = id` ═══
 *
 * Am Stand aufgehängt baute sich das Formular bei jedem Auffrischen neu auf —
 * halb Getipptes war weg. Nur an der ID aufgehängt hätte es nach einem
 * Konflikt alte Werte gegen den frischen Stand verglichen: Ein Feld, das ein
 * Kollege inzwischen geändert hat, stünde im Formular noch mit dem alten Wert
 * da, gälte als „geändert" und würde beim zweiten Speichern still
 * zurückgedreht.
 *
 * Ohne React und ohne Supabase, damit `npm run logik` es prüfen kann
 * (`pruefung/ausgangsstand.test.ts`). Deshalb der Import mit Endung: Node lädt
 * die Datei im Test direkt und rät keine Endungen.
 */

import { zahlLesen } from './zahl.ts';

/**
 * Ein Formular als flache Liste von Feldern. Werte sind Text (wie im
 * Eingabefeld) oder null. Mehrteiliges — die Besetzung je Fahrt — bekommt je
 * Teil ein eigenes Feld (`fahrer:<fahrt-id>`), damit eine neue oder
 * verschwundene Fahrt wie jedes andere Feld dazukommt oder wegfällt.
 */
export type Felder = Record<string, string | null>;

export type Konflikt = { feld: string; frischerWert: string | null };

export type Formularstand = {
  ausgang: Felder;
  eingaben: Felder;
  konflikte: Konflikt[];
};

export type Vergleich = {
  /** Felder, die nach ihrer Bedeutung als Zahl verglichen werden. */
  zahlen?: string[];
};

/**
 * Bedeuten zwei Feldwerte dasselbe?
 *
 * Text wird ohne Leerraum am Rand verglichen, und null gilt wie leer — beim
 * Speichern wird ohnehin getrimmt und Leeres zu null. Sonst meldete die Notiz
 * nach dem Speichern von „Ruft an " einen Konflikt mit sich selbst. Zahlen
 * werden nach Bedeutung verglichen: „12,50" und „12.5" sind derselbe Preis.
 * Unsinn ist keine Zahl und gleicht nur demselben Unsinn.
 *
 * Und auf den Cent: Preis und Menge sind in der Datenbank `numeric(10,2)`.
 * Aus „25,125" wird beim Speichern 25.13 — ohne Rundung im Vergleich stünde
 * nach dem nächsten Auffrischen „Inzwischen geändert auf 25,13" an einem
 * Wert, den man selbst gespeichert hat.
 */
export function gleich(
  feld: string,
  a: string | null,
  b: string | null,
  vergleich: Vergleich = {},
): boolean {
  const links = (a ?? '').trim();
  const rechts = (b ?? '').trim();
  if (vergleich.zahlen?.includes(feld)) {
    const x = zahlLesen(links);
    const y = zahlLesen(rechts);
    if (x.art !== 'ungueltig' && y.art !== 'ungueltig') {
      const aufCent = (z: typeof x) => (z.art === 'leer' ? null : Math.round(z.wert * 100));
      return aufCent(x) === aufCent(y);
    }
  }
  return links === rechts;
}

/** Nur die Felder, die der Nutzer gegenüber dem Ausgangsstand geändert hat. */
export function eigeneAenderungen(
  ausgang: Felder,
  eingaben: Felder,
  vergleich: Vergleich = {},
): Felder {
  const geaendert: Felder = {};
  for (const [feld, wert] of Object.entries(eingaben)) {
    if (!gleich(feld, ausgang[feld] ?? null, wert, vergleich)) geaendert[feld] = wert;
  }
  return geaendert;
}

/**
 * Einen frischen Stand in ein offenes Formular einarbeiten.
 *
 * Je Feld:
 * - nicht angefasst          → folgt dem frischen Wert (Ausgang und Eingabe)
 * - angefasst, nicht fremd   → Eingabe bleibt, der Ausgang ist schon frisch
 * - angefasst UND fremd, zum selben Wert → nichts mehr zu tun, Ausgang frisch
 * - angefasst UND fremd, verschieden     → Konflikt; Eingabe und alter Ausgang
 *   bleiben, damit er auch bei einem noch frischeren Stand offen bleibt
 *
 * Felder, die der frische Stand nicht mehr hat (eine gelöschte Fahrt), fallen
 * weg — auch mit eigener Eingabe: Eine Besetzung für eine Fahrt, die es nicht
 * mehr gibt, kann man nicht speichern. Neue Felder kommen mit ihrem Wert dazu.
 *
 * `offen` sind die Konflikte, die bisher am Formular stehen. Ein solches Feld
 * gilt immer als angefasst — auch wenn man darin bewusst den alten Wert
 * wieder eingetippt hat. Sonst folgte es beim nächsten Auffrischen still dem
 * fremden Wert, und die Entscheidung, die man gerade getroffen hat, wäre weg.
 *
 * Ein unberührtes Feld, an dem sich auch fremd nichts geändert hat, behält
 * seine Eingabe wörtlich: Wer gerade „Kies " tippt, verliert sonst beim
 * Auffrischen das Leerzeichen vor dem nächsten Wort.
 */
export function abgleichen(
  ausgang: Felder,
  eingaben: Felder,
  frisch: Felder,
  vergleich: Vergleich = {},
  offen: Konflikt[] = [],
): Formularstand {
  const neuerAusgang: Felder = {};
  const neueEingaben: Felder = {};
  const konflikte: Konflikt[] = [];

  for (const [feld, frischerWert] of Object.entries(frisch)) {
    if (!(feld in eingaben)) {
      neuerAusgang[feld] = frischerWert;
      neueEingaben[feld] = frischerWert;
      continue;
    }
    const alt = ausgang[feld] ?? null;
    const eingabe = eingaben[feld];
    const eigen =
      offen.some((k) => k.feld === feld) || !gleich(feld, alt, eingabe, vergleich);
    const fremd = !gleich(feld, alt, frischerWert, vergleich);

    if (!eigen && !fremd) {
      neuerAusgang[feld] = frischerWert;
      neueEingaben[feld] = eingabe;
    } else if (!eigen) {
      neuerAusgang[feld] = frischerWert;
      neueEingaben[feld] = frischerWert;
    } else if (!fremd || gleich(feld, eingabe, frischerWert, vergleich)) {
      neuerAusgang[feld] = frischerWert;
      neueEingaben[feld] = eingabe;
    } else {
      neuerAusgang[feld] = alt;
      neueEingaben[feld] = eingabe;
      konflikte.push({ feld, frischerWert });
    }
  }

  return { ausgang: neuerAusgang, eingaben: neueEingaben, konflikte };
}

/**
 * Eine Eingabe ins Formular schreiben.
 *
 * Steht an dem Feld ein Konflikt und trifft die Eingabe jetzt den fremden
 * Wert, ist er gelöst — man hat ihn selbst übernommen. Ohne das bliebe
 * Speichern gesperrt, bis man zusätzlich „Übernehmen" klickt, obwohl schon
 * dasselbe dasteht.
 */
export function eingeben(
  stand: Formularstand,
  feld: string,
  wert: string | null,
  vergleich: Vergleich = {},
): Formularstand {
  const neu = { ...stand, eingaben: { ...stand.eingaben, [feld]: wert } };
  const konflikt = stand.konflikte.find((k) => k.feld === feld);
  if (konflikt && gleich(feld, wert, konflikt.frischerWert, vergleich)) {
    return uebernehmen(neu, feld);
  }
  return neu;
}

/** Konflikt lösen: den fremden Wert übernehmen. */
export function uebernehmen(stand: Formularstand, feld: string): Formularstand {
  const konflikt = stand.konflikte.find((k) => k.feld === feld);
  if (!konflikt) return stand;
  return {
    ausgang: { ...stand.ausgang, [feld]: konflikt.frischerWert },
    eingaben: { ...stand.eingaben, [feld]: konflikt.frischerWert },
    konflikte: stand.konflikte.filter((k) => k.feld !== feld),
  };
}

/**
 * Konflikt lösen: die eigene Eingabe behalten. Der Ausgang rückt auf den
 * frischen Wert — damit bleibt die Eingabe eine Änderung, und gespeichert
 * wird sie gegen den aktuellen Stand, nicht gegen den von vorhin.
 */
export function behalten(stand: Formularstand, feld: string): Formularstand {
  const konflikt = stand.konflikte.find((k) => k.feld === feld);
  if (!konflikt) return stand;
  return {
    ausgang: { ...stand.ausgang, [feld]: konflikt.frischerWert },
    eingaben: stand.eingaben,
    konflikte: stand.konflikte.filter((k) => k.feld !== feld),
  };
}
